import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useSelector } from "react-redux";
import { RootState, Task, TaskType } from "../../redux/type";
import {
  addTask,
  deleteTask,
  getAllTasks,
  getTaskByName,
} from "../../api/task";
import toast from "react-hot-toast";
import AdminNav from "../../components/Admin/AdminNav";
import { MarginBottom } from "../../components/MarginBottom";
import { motion } from "framer-motion";

// --- Main Container and Tabs ---
const Container = styled.div`
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h2`
  color: #ccff00;
  text-align: center;
  font-size: 1.8rem;
  margin-bottom: 2rem;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  margin-bottom: 2rem;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  color: ${(props) => (props.active ? "#fff" : "rgba(255, 255, 255, 0.5)")};
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;

  &:after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: #ccff00;
    transform: ${(props) => (props.active ? "scaleX(1)" : "scaleX(0)")};
    transition: transform 0.3s ease-out;
  }
`;

// --- STYLED COMPONENTS FROM YOUR ORIGINAL AddTaskForm ---
const AddTaskFormContainer = styled.div`
  max-width: 700px;
  padding: 1.5rem;
  border-radius: 12px;
  background: #1a1a1a;
  color: #fff;
  margin: 0 auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;
const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;
const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
`;
const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.95rem;
`;
const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.95rem;
  min-height: 100px;
  resize: vertical;
`;
const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 0.95rem;
`;
const SubmitButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #6ee7b7, #3b82f6);
  color: #111;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
`;

// --- STYLED COMPONENTS FROM YOUR ORIGINAL AdminTasks ---
const fadeIn = keyframes` from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } `;
const TableContainer = styled.div`
  overflow-x: auto;
  border-radius: 12px;
  background: rgba(23, 22, 22, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  animation: ${fadeIn} 0.5s ease-out;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: white;
  min-width: 900px;
`;
const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #ccff00;
  background: rgba(0, 224, 255, 0.1);
  position: sticky;
  top: 0;
  backdrop-filter: blur(5px);
`;
const TableRow = styled(motion.tr)`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`;
const TableCell = styled.td`
  padding: 1rem;
  text-align: left;
`;
const DeleteButton = styled(motion.button)`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  background: linear-gradient(135deg, #ff4d4d, #cc0000);
  color: white;
  border: none;
  font-weight: bold;
  cursor: pointer;
  &:hover {
    background: linear-gradient(135deg, #ff6666, #e60000);
  }
`;
const LoadingIndicator = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #ccff00;
  animation: spin 1s ease-in-out infinite;
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
const EmptyState = styled.div`
  padding: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
`;
const Badge = styled.span<{ $active?: boolean }>`
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${(props) =>
    props.$active ? "rgba(0, 255, 0, 0.1)" : "rgba(255, 0, 0, 0.1)"};
  color: ${(props) => (props.$active ? "#ccff00" : "#ff4d4d")};
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledSelect = styled.div`
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.3);
  }

  &.open {
    border-color: #6ee7b7;
    box-shadow: 0 0 0 3px rgba(110, 231, 183, 0.15);
  }
`;

const SelectArrow = styled.span`
  transition: transform 0.2s ease;
  display: flex;

  &.open {
    transform: rotate(180deg);
  }
`;

const OptionsList = styled.ul`
  position: absolute;
  width: 100%;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 0.5rem;
  padding: 0.5rem 0;
  border-radius: 8px;
  background: #1a1a1a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 10;
  list-style: none;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;

  &.open {
    opacity: 1;
    visibility: visible;
  }
`;

const OptionItem = styled.li`
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.1s ease;
  color: rgba(255, 255, 255, 0.9);

  &:hover {
    background: rgba(110, 231, 183, 0.15);
    color: #6ee7b7;
  }

  &.selected {
    background: linear-gradient(
      135deg,
      rgba(110, 231, 183, 0.3),
      rgba(59, 130, 246, 0.3)
    );
    color: #ccff00;
    font-weight: 500;
  }
`;

// --- COMPONENT: AddTaskPanel ---
const AddTaskPanel = ({ onTaskAdded }: { onTaskAdded: () => void }) => {
  const user = useSelector((state: RootState) => state.userData);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Task>({
    title: "",
    description: "",
    type: TaskType.DECODER,
    xpReward: 10,
    timeReward: 1,
    link: "",
    answer: "",
    isActive: true,
  });
  const [selectedValue, setSelectedValue] = useState(TaskType.DECODER);

  const [isOpen, setIsOpen] = useState(false);
  const toggleMenu = () => setIsOpen(!isOpen);

  const handleSelect = (value: TaskType) => {
    setSelectedValue(value);
    setFormData((prev) => ({ ...prev, type: value }));
    setIsOpen(false);
  };
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const toastId = toast.loading("Adding task...");
    try {
      await addTask({ ...formData, uid: user.uid, isActive: true });
      toast.success("Task added successfully!", { id: toastId });
      onTaskAdded(); // Notify parent to switch tab
    } catch (error: any) {
      toast.error(error.message, { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AddTaskFormContainer>
      <form onSubmit={handleSubmit}>
        <FormGroup>
          <Label>Task Type</Label>
          <SelectWrapper>
            <StyledSelect
              className={isOpen ? "open" : ""}
              onClick={toggleMenu}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
            >
              {getTaskByName(selectedValue)}
              <SelectArrow className={isOpen ? "open" : ""}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#ccff00">
                  <path d="M7 10l5 5 5-5z" />
                </svg>
              </SelectArrow>
            </StyledSelect>

            <OptionsList className={isOpen ? "open" : ""} role="listbox">
              {Object.entries(TaskType).map(([key, value]) => (
                <OptionItem
                  key={key}
                  className={value === selectedValue ? "selected" : ""}
                  onClick={() => handleSelect(value)}
                  role="option"
                  aria-selected={value === selectedValue}
                >
                  {getTaskByName(value)}
                </OptionItem>
              ))}
            </OptionsList>
          </SelectWrapper>
        </FormGroup>
        <FormGroup>
          <Label>Title</Label>
          <Input
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            required
          />
        </FormGroup>
        <FormGroup>
          <Label>Description</Label>
          <TextArea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label>XP Reward</Label>
          <Input
            type="number"
            name="xpReward"
            value={formData.xpReward}
            onChange={handleInputChange}
            required
            min="0"
          />
        </FormGroup>
        <FormGroup>
          <Label>Time Reward (Hours)</Label>
          <Input
            type="number"
            name="timeReward"
            value={formData.timeReward}
            onChange={handleInputChange}
            required
            min="0"
          />
        </FormGroup>
        <SubmitButton type="submit" disabled={loading}>
          {loading ? "Adding..." : "Add Task"}
        </SubmitButton>
      </form>
    </AddTaskFormContainer>
  );
};

// --- COMPONENT: ViewTasksPanel ---
const ViewTasksPanel = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const user = useSelector((state: RootState) => state.userData);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const data = await getAllTasks(user.uid);
      setTasks(data || []);
    } catch {
      toast.error("Failed to fetch tasks");
    }
    setLoading(false);
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm("Are you sure you want to disable this task?")) return;
    setDeleteLoading(true);
    setSelectedTaskId(taskId);
    try {
      await deleteTask(taskId, user.uid);
      toast.success("Task disabled");
      fetchTasks();
    } catch {
      toast.error("Failed to disable task");
    }
    setDeleteLoading(false);
    setSelectedTaskId(null);
  };

  useEffect(() => {
    fetchTasks();
  }, [user]);

  return (
    <TableContainer>
      <Table>
        <thead>
          <tr>
            <TableHeader>Title</TableHeader>
            <TableHeader>Type</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader>Status</TableHeader>
            <TableHeader>Actions</TableHeader>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={7} style={{ textAlign: "center" }}>
                <LoadingIndicator />
              </TableCell>
            </TableRow>
          ) : tasks.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7}>
                <EmptyState>No tasks found</EmptyState>
              </TableCell>
            </TableRow>
          ) : (
            tasks.map((task, index) => (
              <TableRow
                key={task._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
              >
                <TableCell>{task.title}</TableCell>
                <TableCell>{task.type}</TableCell>
                <TableCell>{task.description}</TableCell>
                <TableCell>
                  <Badge $active={task.isActive}>
                    {task.isActive ? "Active" : "Inactive"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <DeleteButton
                    onClick={() => handleDeleteTask(task._id!)}
                    disabled={deleteLoading && selectedTaskId === task._id}
                  >
                    {deleteLoading && selectedTaskId === task._id ? (
                      <LoadingIndicator />
                    ) : (
                      "Disable"
                    )}
                  </DeleteButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </tbody>
      </Table>
    </TableContainer>
  );
};

// --- MAIN PAGE COMPONENT ---
const AdminManageTasks = () => {
  const [activeTab, setActiveTab] = useState("add");
  return (
    <>
      <AdminNav />
      <Container>
        <Title>Manage Tasks</Title>
        <TabsContainer>
          <TabButton
            active={activeTab === "add"}
            onClick={() => setActiveTab("add")}
          >
            Add New Task
          </TabButton>
          <TabButton
            active={activeTab === "view"}
            onClick={() => setActiveTab("view")}
          >
            View All Tasks
          </TabButton>
        </TabsContainer>

        {activeTab === "add" && (
          <AddTaskPanel onTaskAdded={() => setActiveTab("view")} />
        )}
        {activeTab === "view" && <ViewTasksPanel />}
      </Container>
      <MarginBottom />
    </>
  );
};

export default AdminManageTasks;
